import { useState, useEffect, useCallback } from 'react';
import { rewardsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Gift, Check, Ban, Ticket, Plus, X, Power } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EmpfehlungAktionKarte from '@/components/EmpfehlungAktionKarte';

const STATUS = {
  'offen': { label: 'Offen', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  'eingelöst': { label: 'Eingelöst', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  'storniert': { label: 'Storniert', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

function dateDE(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

export default function PraemienPage() {
  const [redemptions, setRedemptions] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [catalog, setCatalog] = useState([]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ emoji: '🎁', title: '', description: '', points: 100, sort_order: 99 });

  const fetchData = useCallback(async () => {
    try {
      const [rRes, cRes] = await Promise.all([rewardsAPI.redemptions(), rewardsAPI.catalog()]);
      setRedemptions(rRes.data.redemptions || []);
      setOpenCount(rRes.data.open_count || 0);
      setCatalog(cRes.data.rewards || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFulfill = async (c) => {
    const val = (c || code).trim();
    if (!val) return;
    setBusy(true);
    try {
      const res = await rewardsAPI.fulfill(val);
      toast.success(`Eingelöst: ${res.data.reward_title} (${res.data.member_name})`);
      setCode('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Code nicht einlösbar');
    } finally { setBusy(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Einlösung stornieren? Die Punkte werden dem Mitglied wieder gutgeschrieben.')) return;
    try { await rewardsAPI.cancelRedemption(id); toast.success('Storniert'); fetchData(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Fehler'); }
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.points) { toast.error('Titel und Punkte erforderlich'); return; }
    try {
      await rewardsAPI.createReward({ ...form, points: Number(form.points), sort_order: Number(form.sort_order) });
      toast.success('Prämie angelegt');
      setShowAdd(false);
      setForm({ emoji: '🎁', title: '', description: '', points: 100, sort_order: 99 });
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Fehler'); }
  };

  const toggleActive = async (r) => {
    try {
      if (r.active) { await rewardsAPI.deactivateReward(r.id); }
      else { await rewardsAPI.updateReward(r.id, { ...r, active: true }); }
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Fehler'); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="praemien-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Gift className="w-6 h-6 text-primary" /> Prämien</h1>
        <p className="text-muted-foreground text-sm">Einlöse-Codes an der Theke einlösen · Katalog verwalten</p>
      </div>

      {/* Code einlösen — Haupt-Theken-Aktion */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Code einlösen</h2>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Code (z. B. P1-A1B2C3)"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleFulfill()}
              className="h-12 text-base font-mono"
              data-testid="praemien-code-input"
            />
            <Button className="h-12 px-6" onClick={() => handleFulfill()} disabled={busy || !code.trim()} data-testid="praemien-fulfill-btn">
              <Check className="w-5 h-5 mr-1.5" /> Einlösen
            </Button>
          </div>
        </CardContent>
      </Card>

      <EmpfehlungAktionKarte />

      <Tabs defaultValue="redemptions" className="w-full">
        <TabsList>
          <TabsTrigger value="redemptions" data-testid="tab-redemptions">Einlösungen{openCount > 0 && <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded-full px-1.5">{openCount}</span>}</TabsTrigger>
          <TabsTrigger value="catalog" data-testid="tab-catalog">Katalog</TabsTrigger>
        </TabsList>

        {/* Einlösungen */}
        <TabsContent value="redemptions" className="mt-4 space-y-2">
          {redemptions.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10">Noch keine Einlösungen.</p>
          ) : redemptions.map((r) => {
            const st = STATUS[r.status] || { label: r.status, cls: 'bg-muted' };
            return (
              <Card key={r.id} className="border-border/60">
                <CardContent className="p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xl flex-shrink-0">{r.reward_emoji || '🎁'}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.reward_title}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.member_name} · <span className="font-mono">{r.code}</span> · {dateDE(r.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    {r.status === 'offen' && (
                      <>
                        <Button size="sm" className="h-8" onClick={() => handleFulfill(r.code)} data-testid={`fulfill-${r.id}`}><Check className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={() => handleCancel(r.id)} data-testid={`cancel-${r.id}`}><Ban className="w-4 h-4" /></Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Katalog */}
        <TabsContent value="catalog" className="mt-4 space-y-2">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowAdd(v => !v)} data-testid="praemien-add-btn">
              {showAdd ? <><X className="w-4 h-4 mr-1" /> Abbrechen</> : <><Plus className="w-4 h-4 mr-1" /> Prämie hinzufügen</>}
            </Button>
          </div>
          {showAdd && (
            <Card className="border-border/60">
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-[60px_1fr] gap-2">
                  <Input placeholder="🎁" value={form.emoji} onChange={(e) => setForm(f => ({ ...f, emoji: e.target.value }))} className="text-center" />
                  <Input placeholder="Titel (z. B. Gratis Shake)" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <Input placeholder="Beschreibung" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Punkte" value={form.points} onChange={(e) => setForm(f => ({ ...f, points: e.target.value }))} />
                  <Input type="number" placeholder="Reihenfolge" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
                </div>
                <Button className="w-full" onClick={handleCreate} data-testid="praemien-save-btn">Speichern</Button>
              </CardContent>
            </Card>
          )}
          {catalog.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10">Noch keine Prämien im Katalog.</p>
          ) : catalog.map((r) => (
            <Card key={r.id} className={`border-border/60 ${!r.active ? 'opacity-50' : ''}`}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl flex-shrink-0">{r.emoji || '🎁'}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{r.title}</p>
                    {r.description && <p className="text-xs text-muted-foreground truncate">{r.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-sm font-bold text-primary whitespace-nowrap">{r.points} P{r.wert_euro ? <span className="ml-1 text-xs font-normal text-muted-foreground">· {Number(r.wert_euro).toLocaleString('de-DE')} €</span> : null}</span>
                  <Button size="sm" variant="ghost" className={`h-8 ${r.active ? 'text-emerald-600' : 'text-muted-foreground'}`} onClick={() => toggleActive(r)} title={r.active ? 'Deaktivieren' : 'Aktivieren'} data-testid={`toggle-${r.id}`}>
                    <Power className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
